import { useMutation, useQueryClient } from '@tanstack/react-query'
import { setDiscussFlag, DEFAULT_META } from '../data/metaApi.js'
import { withArchiveGuard } from './_archiveGuard.js'

/**
 * useDiscussFlag — toggle the "discuss this week" flag on a goal.
 *
 * Anyone (not just the DRI) can flip a flag; it's the cross-team callout
 * channel for the Wednesday review. Resets every Wednesday wipe.
 *
 * Optimistic: writes to meta.discussFlags[goalKey] immediately so the
 * scorecard re-renders without waiting for the server. onSuccess replaces
 * with the server's authoritative map.
 *
 * Usage:
 *   const flag = useDiscussFlag()
 *   flag.mutate({ goalKey: 'p0-g1', value: true })
 */
export function useDiscussFlag() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({ goalKey, value }) => {
      const flags = await setDiscussFlag({ goalKey, value })
      return flags
    },

    onMutate: async ({ goalKey, value }) => {
      await queryClient.cancelQueries({ queryKey: ['meta'] })
      const previous = queryClient.getQueryData(['meta']) || DEFAULT_META

      const nextFlags = { ...(previous.discussFlags || {}) }
      if (value) {
        nextFlags[goalKey] = true
      } else {
        delete nextFlags[goalKey]
      }
      queryClient.setQueryData(['meta'], {
        ...previous,
        discussFlags: nextFlags,
      })
      return { previous }
    },

    onSuccess: (discussFlags) => {
      const current = queryClient.getQueryData(['meta']) || DEFAULT_META
      queryClient.setQueryData(['meta'], {
        ...current,
        discussFlags: discussFlags || {},
      })
    },

    onError: (err, vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['meta'], ctx.previous)
    },
  })

  return withArchiveGuard(mutation)
}
